<template>
    <!-- Step 1: QR Code + Security Code -->
    <Dialog v-if="recoveryCodes.length === 0" :open="true" @update:open="closeModal">
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>Enable Authenticator App</DialogTitle>
            </DialogHeader>

            <div class="space-y-6">
                <p class="text-sm text-muted-foreground">
                    Make your account safer in 3 easy steps:
                </p>

                <!-- Step 1: Download App -->
                <div class="flex items-start gap-4">
                    <div class="flex size-8 shrink-0 items-center justify-center rounded-full bg-new-neutral-100 text-sm font-medium">1</div>
                    <div>
                        <p class="text-sm font-medium">Download an authenticator app</p>
                        <p class="text-xs text-muted-foreground">
                            Download and install an authenticator app such as Google Authenticator or Authy.
                        </p>
                    </div>
                </div>

                <!-- Step 2: Scan QR -->
                <div class="flex items-start gap-4">
                    <div class="flex size-8 shrink-0 items-center justify-center rounded-full bg-new-neutral-100 text-sm font-medium">2</div>
                    <div class="space-y-3">
                        <div>
                            <p class="text-sm font-medium">Scan the QR code</p>
                            <p class="text-xs text-muted-foreground">
                                Open the authenticator app and scan the QR code below.
                            </p>
                        </div>
                        <div v-if="paringCodes.qrCode" class="flex justify-center rounded-md border p-4" v-html="paringCodes.qrCode"></div>
                        <div v-else class="flex h-[200px] items-center justify-center rounded-md border">
                            <div class="size-8 animate-spin rounded-full border-2 border-new-neutral-300 border-t-new-neutral-900"></div>
                        </div>
                    </div>
                </div>

                <!-- Step 3: Enter Code -->
                <div class="flex items-start gap-4">
                    <div class="flex size-8 shrink-0 items-center justify-center rounded-full bg-new-neutral-100 text-sm font-medium">3</div>
                    <div class="flex-1 space-y-3">
                        <div>
                            <p class="text-sm font-medium">Sign in with your security code</p>
                            <p class="text-xs text-muted-foreground">
                                Enter the security code from your authenticator app.
                            </p>
                        </div>
                        <div class="w-[250px]">
                            <Input
                                v-model="form.code"
                                placeholder="Security Code"
                                :class="{ 'border-destructive': error }"
                            />
                            <p v-if="error" class="mt-1 text-xs text-destructive">{{ error }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="closeModal">Cancel</Button>
                <ConfirmsPasswordV2
                    v-if="hasPassword"
                    :force-confirmation="true"
                    @confirmed="confirmTwoFactorAuthentication"
                >
                    <Button size="lg" :disabled="form.processing || !form.code">
                        Activate 2FA
                    </Button>
                </ConfirmsPasswordV2>
                <Button
v-else
                    size="lg"
                    :disabled="form.processing || !form.code"
                    @click="confirmTwoFactorAuthentication('')"
                >
                    Activate 2FA
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>

    <!-- Step 2: Recovery Codes (shown after successful activation) -->
    <TwoFactorRecoveryCodesDialogV2
        v-if="recoveryCodes.length > 0"
        title="Two-Factor Authentication enabled"
        :recovery-codes="recoveryCodes"
        :can-regenerate="false"
        @close="closeModal"
    />
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import useParingCodes from '@/Pages/Settings/Profile/composable/usePairingCodes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import ConfirmsPasswordV2 from './ConfirmsPasswordV2.vue';
import TwoFactorRecoveryCodesDialogV2 from './TwoFactorRecoveryCodesDialogV2.vue';

const props = defineProps<{
    hasPassword: boolean;
}>();

const emit = defineEmits<{
    close: [];
    enabled: [];
}>();

const { paringCodes } = useParingCodes('auth.prepare-two-factor-authentication');

const form = reactive({
    processing: false,
    code: '',
});

const error = ref('');
const recoveryCodes = ref<string[]>([]);

const confirmTwoFactorAuthentication = (password: string) => {
    form.processing = true;
    error.value = '';

    axios
        .post(route('auth.enable-two-factor-authentication'), {
            code: form.code,
            secret: paringCodes.key,
            password,
        })
        .then((response) => {
            form.processing = false;
            if (response.data.data) {
                emit('enabled');
                recoveryCodes.value = response.data.data;
            } else {
                error.value = 'Invalid code';
            }
        })
        .catch((err) => {
            form.processing = false;
            error.value = err.response?.data?.errors?.code?.[0] || 'Invalid code';
        });
};

const closeModal = () => {
    recoveryCodes.value = [];
    emit('close');
};
</script>
